/**
 * Capa mínima de compatibilidad con WebMCP.
 *
 * WebMCP sigue siendo una mejora progresiva: si el navegador no ofrece
 * `document.modelContext`, la UI humana y el fallback HTML/ARIA siguen
 * funcionando exactamente igual. No se exponen herramientas cross-origin.
 */

export interface AgentJsonSchema {
  type: 'object';
  properties: Record<string, AgentJsonSchemaProperty>;
  required?: string[];
  additionalProperties?: boolean;
}

export interface AgentJsonSchemaProperty {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array';
  description: string;
  enum?: readonly (string | number | boolean)[];
  items?: AgentJsonSchemaProperty;
  minItems?: number;
  maxItems?: number;
  minimum?: number;
  maximum?: number;
  maxLength?: number;
}

/**
 * Orienta al agente y a su interfaz de permisos sin decidir por la persona.
 *
 * El nivel describe las consecuencias de la acción; no implica permitirla,
 * denegarla ni exigir una confirmación concreta. Esa decisión pertenece al
 * usuario que delega.
 */
export type AgentDecisionImportance =
  | 'routine'
  | 'important'
  | 'sensitive'
  | 'critical';

/** Modos comunes que una interfaz de usuario puede ofrecer por scope. */
export const AGENT_PERMISSION_MODES = [
  'deny',
  'ask',
  'session',
  'until',
  'persistent',
] as const;

export type AgentPermissionMode = (typeof AGENT_PERMISSION_MODES)[number];

export interface AgentDelegationHint {
  /** Scope estable que una interfaz de delegación puede mostrar o almacenar. */
  permissionScope: string;
  /** Importancia informativa para ayudar a tomar una decisión consciente. */
  importance: AgentDecisionImportance;
  /** Explicación breve de la consecuencia principal. */
  reason: string;
}

export interface WebMcpTool<Input extends Record<string, unknown> = Record<string, unknown>> {
  name: string;
  /** Etiqueta humana breve que puede mostrar la UI nativa del agente. */
  title: string;
  description: string;
  inputSchema: AgentJsonSchema;
  /**
   * Metadatos de delegación propios de Ibercar. Se convierten en una pista
   * breve para el agente, pero no se usan para sustituir la decisión del
   * usuario ni se envían como un campo no estándar a WebMCP.
   */
  delegation?: AgentDelegationHint;
  annotations?: {
    readOnlyHint?: boolean;
    untrustedContentHint?: boolean;
  };
  execute: (input: Input) => unknown | Promise<unknown>;
}

interface WebMcpModelContext {
  registerTool: (
    tool: WebMcpTool,
    options?: {
      signal?: AbortSignal;
      exposedTo?: string[];
    },
  ) => void | Promise<void>;
}

export interface WebMcpDocument {
  modelContext?: WebMcpModelContext;
}

export interface WebMcpRegistration {
  /** `false` en navegadores sin WebMCP; el fallback semántico permanece activo. */
  supported: boolean;
  /** Permite a tests y diagnósticos esperar a que el navegador acepte el tool. */
  ready: Promise<boolean>;
  /** Idempotente. Retira el tool mediante AbortSignal. */
  unregister: () => void;
}

export interface RegisterWebMcpOptions {
  document?: WebMcpDocument;
  /**
   * Orígenes seguros elegidos por la configuración de delegación del usuario.
   * Si se omite, WebMCP conserva su aislamiento same-origin.
   */
  exposedTo?: readonly string[];
  onError?: (error: unknown) => void;
}

const MAX_TOOL_NAME_LENGTH = 30;
const MAX_TOOL_TITLE_LENGTH = 80;
const MAX_TOOL_DESCRIPTION_LENGTH = 500;
const MAX_PARAMETER_NAME_LENGTH = 30;
const MAX_PARAMETER_DESCRIPTION_LENGTH = 150;
const MAX_TOOL_OUTPUT_LENGTH = 1_500;
const MAX_PUBLIC_ERROR_MESSAGE_LENGTH = 500;
const MAX_PERMISSION_SCOPE_LENGTH = 80;
const MAX_DELEGATION_REASON_LENGTH = 150;
const MAX_EXPOSED_ORIGINS = 10;
const TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
const PERMISSION_SCOPE_PATTERN =
  /^[a-z][a-z0-9_]*(?:[.:][a-z][a-z0-9_]*)+$/;
const TOOL_EXECUTION_ERROR_CODE = 'tool_execution_failed';
const TOOL_EXECUTION_ERROR_MESSAGE =
  'No se pudo completar la acción. Revisa los datos e inténtalo de nuevo.';

/**
 * Error deliberadamente seguro y accionable que un tool puede mostrar al
 * agente. Su código y mensaje están acotados; nunca conserva cause ni stack.
 */
export class WebMcpPublicError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    const normalizedMessage = message.replace(/\s+/g, ' ').trim();
    if (
      !TOOL_NAME_PATTERN.test(code) ||
      code.length > MAX_TOOL_NAME_LENGTH
    ) {
      throw new Error('El código de error WebMCP público no es válido.');
    }
    if (
      normalizedMessage.length === 0 ||
      normalizedMessage.length > MAX_PUBLIC_ERROR_MESSAGE_LENGTH
    ) {
      throw new Error(
        `El mensaje WebMCP público debe tener entre 1 y ${MAX_PUBLIC_ERROR_MESSAGE_LENGTH} caracteres.`,
      );
    }
    super(`[${code}] ${normalizedMessage}`);
    this.name = 'WebMcpPublicError';
    this.code = code;
    this.stack = undefined;
  }

  toJSON(): { code: string; message: string } {
    return {
      code: this.code,
      message: this.message,
    };
  }
}

/**
 * Error público estable para fallos internos de una herramienta.
 *
 * No conserva `cause`, el valor rechazado ni una traza. Así, un proveedor
 * externo puede seguir registrando el error original mediante `onError`, pero
 * el agente solo recibe información breve y segura para decidir su siguiente
 * paso.
 */
export class WebMcpToolExecutionError extends WebMcpPublicError {
  constructor() {
    super(TOOL_EXECUTION_ERROR_CODE, TOOL_EXECUTION_ERROR_MESSAGE);
    this.name = 'WebMcpToolExecutionError';
  }
}

/**
 * Registra un tool solo cuando el navegador soporta WebMCP. El helper nunca
 * añade `exposedTo`: las herramientas quedan limitadas al origen y al agente
 * integrado/autorizado por el navegador.
 */
export function registerWebMcpTool<Input extends Record<string, unknown>>(
  tool: WebMcpTool<Input>,
  options: RegisterWebMcpOptions = {},
): WebMcpRegistration {
  const documentLike =
    options.document ??
    (typeof document === 'undefined' ? undefined : (document as unknown as WebMcpDocument));
  const modelContext = documentLike?.modelContext;

  if (!modelContext) {
    return {
      supported: false,
      ready: Promise.resolve(false),
      unregister: () => undefined,
    };
  }

  try {
    assertValidWebMcpTool(tool);
    assertValidExposedOrigins(options.exposedTo);
  } catch (error) {
    reportError(options.onError, error);
    return {
      supported: true,
      ready: Promise.resolve(false),
      unregister: () => undefined,
    };
  }

  const controller = new AbortController();
  let unregistered = false;
  const guardedTool: WebMcpTool = {
    name: tool.name,
    title: tool.title,
    description: describeToolDelegation(tool),
    inputSchema: tool.inputSchema,
    annotations: tool.annotations,
    execute: async (input) => {
      try {
        assertValidWebMcpInput(tool.inputSchema, input);
      } catch (error) {
        throw toPublicInputError(error);
      }
      try {
        return normalizeWebMcpOutput(await tool.execute(input as Input));
      } catch (error) {
        if (error instanceof WebMcpPublicError) {
          throw error;
        }
        reportError(options.onError, error);
        throw new WebMcpToolExecutionError();
      }
    },
  };
  const ready = Promise.resolve()
    .then(() =>
      modelContext.registerTool(guardedTool, {
        signal: controller.signal,
        ...(options.exposedTo
          ? { exposedTo: [...options.exposedTo] }
          : {}),
      }),
    )
    .then(() => !unregistered)
    .catch((error: unknown) => {
      controller.abort();
      reportError(options.onError, error);
      return false;
    });

  return {
    supported: true,
    ready,
    unregister: () => {
      if (unregistered) return;
      unregistered = true;
      controller.abort();
    },
  };
}

function toPublicInputError(error: unknown): WebMcpPublicError {
  const fallback = 'Revisa los parámetros declarados e inténtalo de nuevo.';
  const rawMessage = error instanceof Error ? error.message : fallback;
  const normalized = rawMessage.replace(/\s+/g, ' ').trim() || fallback;
  const bounded =
    normalized.length <= MAX_PUBLIC_ERROR_MESSAGE_LENGTH
      ? normalized
      : `${normalized.slice(0, MAX_PUBLIC_ERROR_MESSAGE_LENGTH - 1)}…`;
  return new WebMcpPublicError('invalid_input', bounded);
}

/** Valida los presupuestos de contexto recomendados para agentes pequeños. */
export function assertValidWebMcpTool<Input extends Record<string, unknown>>(
  tool: WebMcpTool<Input>,
): void {
  if (!TOOL_NAME_PATTERN.test(tool.name) || tool.name.length > MAX_TOOL_NAME_LENGTH) {
    throw new Error(
      `Nombre WebMCP inválido: usa snake_case ASCII y un máximo de ${MAX_TOOL_NAME_LENGTH} caracteres.`,
    );
  }
  if (
    tool.title.trim().length === 0 ||
    tool.title.length > MAX_TOOL_TITLE_LENGTH
  ) {
    throw new Error(
      `Título WebMCP inválido: debe tener entre 1 y ${MAX_TOOL_TITLE_LENGTH} caracteres.`,
    );
  }
  const registeredDescription = describeToolDelegation(tool);
  if (
    tool.description.trim().length === 0 ||
    registeredDescription.length > MAX_TOOL_DESCRIPTION_LENGTH
  ) {
    throw new Error(
      `Descripción WebMCP inválida: debe tener entre 1 y ${MAX_TOOL_DESCRIPTION_LENGTH} caracteres.`,
    );
  }
  if (tool.delegation) {
    assertValidDelegationHint(tool.delegation);
  }
  if (tool.inputSchema.type !== 'object') {
    throw new Error('El inputSchema WebMCP debe ser un objeto.');
  }
  if (tool.inputSchema.additionalProperties !== false) {
    throw new Error('El inputSchema WebMCP debe declarar additionalProperties: false.');
  }

  for (const [name, property] of Object.entries(tool.inputSchema.properties)) {
    assertValidProperty(name, property);
  }

  for (const requiredName of tool.inputSchema.required ?? []) {
    if (!hasOwn(tool.inputSchema.properties, requiredName)) {
      throw new Error(`Parámetro WebMCP requerido no declarado: ${requiredName}.`);
    }
  }
}

/**
 * Añade al texto público solo información para decidir: scope, importancia y
 * consecuencia. No añade reglas negativas ni presupone el permiso elegido.
 */
export function describeToolDelegation(
  tool: Pick<WebMcpTool, 'description' | 'delegation'>,
): string {
  const description = tool.description.trim();
  if (!tool.delegation) return description;
  const { importance, permissionScope, reason } = tool.delegation;
  return `${description} Permiso: ${permissionScope}. Importancia: ${importance}. ${reason.trim()}`;
}

function assertValidDelegationHint(hint: AgentDelegationHint): void {
  if (
    !PERMISSION_SCOPE_PATTERN.test(hint.permissionScope) ||
    hint.permissionScope.length > MAX_PERMISSION_SCOPE_LENGTH
  ) {
    throw new Error(
      'El permiso delegado debe ser un scope estable con segmentos separados por punto o dos puntos.',
    );
  }
  if (
    !['routine', 'important', 'sensitive', 'critical'].includes(
      hint.importance,
    )
  ) {
    throw new Error('El nivel de importancia delegado no es válido.');
  }
  if (
    hint.reason.trim().length === 0 ||
    hint.reason.length > MAX_DELEGATION_REASON_LENGTH
  ) {
    throw new Error(
      `La explicación del permiso debe tener entre 1 y ${MAX_DELEGATION_REASON_LENGTH} caracteres.`,
    );
  }
}

function assertValidExposedOrigins(origins: readonly string[] | undefined): void {
  if (!origins) return;
  if (origins.length > MAX_EXPOSED_ORIGINS) {
    throw new Error(
      `WebMCP admite como máximo ${MAX_EXPOSED_ORIGINS} orígenes delegados.`,
    );
  }
  if (new Set(origins).size !== origins.length) {
    throw new Error('Los orígenes WebMCP delegados no pueden repetirse.');
  }
  for (const candidate of origins) {
    let parsed: URL;
    try {
      parsed = new URL(candidate);
    } catch {
      throw new Error(`Origen WebMCP delegado inválido: ${candidate}.`);
    }
    const localhost =
      parsed.protocol === 'http:' &&
      (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1');
    if (
      (parsed.protocol !== 'https:' && !localhost) ||
      parsed.origin !== candidate ||
      parsed.username !== '' ||
      parsed.password !== ''
    ) {
      throw new Error(
        `Origen WebMCP delegado inseguro o no canónico: ${candidate}.`,
      );
    }
  }
}

function assertValidProperty(
  name: string,
  property: AgentJsonSchemaProperty,
  validateName = true,
): void {
  if (
    validateName &&
    (!TOOL_NAME_PATTERN.test(name) || name.length > MAX_PARAMETER_NAME_LENGTH)
  ) {
    throw new Error(
      `Nombre de parámetro WebMCP inválido: usa snake_case ASCII y un máximo de ${MAX_PARAMETER_NAME_LENGTH} caracteres.`,
    );
  }
  assertSupportedPropertyType(name, property.type);
  if (
    property.description.trim().length === 0 ||
    property.description.length > MAX_PARAMETER_DESCRIPTION_LENGTH
  ) {
    throw new Error(
      `Descripción WebMCP inválida para ${name}: máximo ${MAX_PARAMETER_DESCRIPTION_LENGTH} caracteres.`,
    );
  }
  if (property.enum !== undefined) {
    if (property.enum.length === 0) {
      throw new Error(`El enum WebMCP de ${name} no puede estar vacío.`);
    }
    if (
      property.type === 'array' ||
      property.enum.some((value) => !enumValueMatchesType(value, property.type))
    ) {
      throw new Error(
        `El enum WebMCP de ${name} contiene valores incompatibles con ${property.type}.`,
      );
    }
  }

  const hasNumericBounds =
    property.minimum !== undefined || property.maximum !== undefined;
  if (
    hasNumericBounds &&
    property.type !== 'number' &&
    property.type !== 'integer'
  ) {
    throw new Error(`Los límites minimum/maximum de ${name} requieren un número.`);
  }
  if (
    property.minimum !== undefined &&
    property.maximum !== undefined &&
    property.minimum > property.maximum
  ) {
    throw new Error(`El límite minimum de ${name} no puede superar maximum.`);
  }

  if (
    property.maxLength !== undefined &&
    (property.type !== 'string' ||
      !Number.isInteger(property.maxLength) ||
      property.maxLength < 0)
  ) {
    throw new Error(`maxLength de ${name} requiere texto y un entero no negativo.`);
  }

  const hasArrayBounds =
    property.minItems !== undefined || property.maxItems !== undefined;
  if (hasArrayBounds && property.type !== 'array') {
    throw new Error(`Los límites minItems/maxItems de ${name} requieren una lista.`);
  }
  for (const bound of [property.minItems, property.maxItems]) {
    if (bound !== undefined && (!Number.isInteger(bound) || bound < 0)) {
      throw new Error(`Los límites de elementos de ${name} deben ser enteros no negativos.`);
    }
  }
  if (
    property.minItems !== undefined &&
    property.maxItems !== undefined &&
    property.minItems > property.maxItems
  ) {
    throw new Error(`minItems de ${name} no puede superar maxItems.`);
  }

  if (property.type === 'array') {
    if (!property.items) {
      throw new Error(`El parámetro WebMCP ${name} debe declarar items.`);
    }
    assertValidProperty(`${name}[]`, property.items, false);
  } else if (property.items !== undefined) {
    throw new Error(`Solo una lista WebMCP puede declarar items en ${name}.`);
  }
}

/**
 * El esquema orienta al agente, pero el navegador no garantiza que el input
 * lo respete. Esta validación se ejecuta siempre antes de la lógica del tool.
 */
export function assertValidWebMcpInput(
  schema: AgentJsonSchema,
  input: unknown,
): asserts input is Record<string, unknown> {
  if (!isRecord(input)) {
    throw new Error('Entrada inválida: envía un objeto JSON.');
  }

  for (const requiredName of schema.required ?? []) {
    if (!hasOwn(input, requiredName)) {
      throw new Error(`Falta el parámetro requerido "${requiredName}".`);
    }
  }

  for (const [name, value] of Object.entries(input)) {
    if (!hasOwn(schema.properties, name)) {
      throw new Error(`Parámetro desconocido "${name}". Usa solo los parámetros declarados.`);
    }
    const property = schema.properties[name]!;
    assertValueMatchesProperty(name, value, property);
  }
}

/**
 * Mantiene el resultado dentro del presupuesto de contexto recomendado. Nunca
 * corta JSON a mitad: si el tool devuelve demasiado, responde con un error
 * pequeño y determinista para que la implementación se corrija.
 */
export function normalizeWebMcpOutput(output: unknown): unknown {
  if (typeof output === 'string') {
    if (output.length <= MAX_TOOL_OUTPUT_LENGTH) return output;
    return `${output.slice(0, MAX_TOOL_OUTPUT_LENGTH - 26)}… [resultado truncado]`;
  }

  let serialized: string | undefined;
  try {
    serialized = JSON.stringify(output);
  } catch {
    throw new Error('El resultado del tool debe poder serializarse como JSON.');
  }

  if (serialized === undefined) {
    throw new Error('El resultado del tool debe ser un valor JSON.');
  }
  if (serialized.length <= MAX_TOOL_OUTPUT_LENGTH) {
    return JSON.parse(serialized) as unknown;
  }

  return {
    ok: false,
    error: {
      code: 'output_too_large',
      message: 'El resultado supera 1500 caracteres. Solicita una respuesta más concreta.',
    },
  };
}

function assertValueMatchesProperty(
  name: string,
  value: unknown,
  property: AgentJsonSchemaProperty,
): void {
  if (property.enum && !property.enum.includes(value as string | number | boolean)) {
    throw new Error(
      `Valor inválido para "${name}". Valores permitidos: ${property.enum.join(', ')}.`,
    );
  }

  switch (property.type) {
    case 'string':
      if (typeof value !== 'string') {
        throw new Error(`El parámetro "${name}" debe ser texto.`);
      }
      if (property.maxLength !== undefined && value.length > property.maxLength) {
        throw new Error(`El parámetro "${name}" supera ${property.maxLength} caracteres.`);
      }
      break;
    case 'number':
    case 'integer':
      if (
        typeof value !== 'number' ||
        !Number.isFinite(value) ||
        (property.type === 'integer' && !Number.isInteger(value))
      ) {
        throw new Error(
          `El parámetro "${name}" debe ser ${property.type === 'integer' ? 'un entero' : 'un número'}.`,
        );
      }
      if (property.minimum !== undefined && value < property.minimum) {
        throw new Error(`El parámetro "${name}" debe ser al menos ${property.minimum}.`);
      }
      if (property.maximum !== undefined && value > property.maximum) {
        throw new Error(`El parámetro "${name}" debe ser como máximo ${property.maximum}.`);
      }
      break;
    case 'boolean':
      if (typeof value !== 'boolean') {
        throw new Error(`El parámetro "${name}" debe ser booleano.`);
      }
      break;
    case 'array':
      if (!Array.isArray(value)) {
        throw new Error(`El parámetro "${name}" debe ser una lista.`);
      }
      if (property.minItems !== undefined && value.length < property.minItems) {
        throw new Error(`El parámetro "${name}" necesita al menos ${property.minItems} elementos.`);
      }
      if (property.maxItems !== undefined && value.length > property.maxItems) {
        throw new Error(`El parámetro "${name}" admite como máximo ${property.maxItems} elementos.`);
      }
      if (property.items) {
        value.forEach((item, index) =>
          assertValueMatchesProperty(`${name}[${index}]`, item, property.items!),
        );
      }
      break;
    default:
      throw new Error(`Tipo WebMCP no compatible para ${name}.`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwn(object: object, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function enumValueMatchesType(
  value: string | number | boolean,
  type: AgentJsonSchemaProperty['type'],
): boolean {
  switch (type) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'array':
      return false;
    default:
      throw new Error('Tipo WebMCP no compatible.');
  }
}

function assertSupportedPropertyType(
  name: string,
  type: unknown,
): asserts type is AgentJsonSchemaProperty['type'] {
  switch (type) {
    case 'string':
    case 'number':
    case 'integer':
    case 'boolean':
    case 'array':
      return;
    default:
      throw new Error(`Tipo WebMCP no compatible para ${name}.`);
  }
}

function reportError(
  onError: RegisterWebMcpOptions['onError'],
  error: unknown,
): void {
  try {
    onError?.(error);
  } catch {
    // Un callback de diagnóstico nunca debe romper la interfaz humana.
  }
}
